import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ButtonSpinner } from '../../components/ui/Loader';
import {
  useGetAllPaymentGatewaysQuery,
  useUpdatePayeerConfigMutation,
  useUpdateCryptoConfigMutation,
  useUpdateSyriaTelConfigMutation,
  useLazyDownloadSyriaTelAutomateFloQuery,
  useUpdateShamCashConfigMutation,
  useUpdateBinanceConfigMutation,
  useUpdateCoinExConfigMutation,
  useUpdateOxaPayConfigMutation,
} from '../../store/api/adminApi';
import { IS_TENANT_MODE } from '../../config/tenantMode';

type GatewayType = 'payeer' | 'crypto' | 'oxapay' | 'syriatel' | 'shamCash' | 'binance' | 'coinex';

export function AdminPaymentGatewaysPage() {
  const { t } = useTranslation();
  const [editingGateway, setEditingGateway] = useState<GatewayType | null>(null);
  const [showShamCashGuide, setShowShamCashGuide] = useState(false);

  // Forms state
  const [payeerForm, setPayeerForm] = useState({
    accountNumber: '',
    merchantId: '',
    secretKey: '',
    minAmount: 0.1,
  });

  const [cryptoForm, setCryptoForm] = useState({
    walletAddress: '',
    platformName: '',
    merchantId: '',
    minAmount: 0.1,
  });

  const [syriatelForm, setSyriatelForm] = useState({
    receptionCodes: [''],
    minAmount: 0.1,
    apiKey: '',
  });

  const [shamCashForm, setShamCashForm] = useState({
    receptionNumber: '',
    minAmount: 0.1,
    accountAddress: '',
    shamGateApiKey: '',
    webhookSecret: '',
  });

  const [binanceForm, setBinanceForm] = useState({
    enabled: false,
    apiKey: '',
    secretKey: '',
    walletAddress: '',
    minAmount: 1,
  });

  const [oxapayForm, setOxapayForm] = useState({
    enabled: false,
    apiKey: '',
    sandbox: false,
    lifetimeMinutes: 60,
    minAmount: 1,
  });

  const [coinexForm, setCoinExForm] = useState({
    enabled: false,
    accessId: '',
    secretKey: '',
    adminEmail: '',
    coin: 'USDT',
    minAmount: 1,
  });

  const { data: gatewaysData, isLoading } = useGetAllPaymentGatewaysQuery();

  const [updatePayeer, { isLoading: isUpdatingPayeer }] = useUpdatePayeerConfigMutation();
  const [updateCrypto, { isLoading: isUpdatingCrypto }] = useUpdateCryptoConfigMutation();
  const [updateSyriaTel, { isLoading: isUpdatingSyriaTel }] = useUpdateSyriaTelConfigMutation();
  const [triggerDownloadFlo, { isFetching: isDownloadingFlo }] = useLazyDownloadSyriaTelAutomateFloQuery();

  const handleDownloadAutomateFlo = async () => {
    try {
      const blob = await triggerDownloadFlo(Date.now(), false).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'SaasRocket.flo';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('admin.paymentGateways.syriatel.automateFileGenerated'));
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.paymentGateways.syriatel.updateError')
      );
    }
  };
  const [updateShamCash, { isLoading: isUpdatingShamCash }] = useUpdateShamCashConfigMutation();
  const [updateBinance, { isLoading: isUpdatingBinance }] = useUpdateBinanceConfigMutation();
  const [updateOxaPay, { isLoading: isUpdatingOxaPay }] = useUpdateOxaPayConfigMutation();
  const [updateCoinEx, { isLoading: isUpdatingCoinEx }] = useUpdateCoinExConfigMutation();

  const handleStartEdit = (gatewayType: GatewayType) => {
    if (!gatewaysData?.data.configs) return;

    setEditingGateway(gatewayType);

    switch (gatewayType) {
      case 'payeer':
        setPayeerForm({
          accountNumber: gatewaysData.data.configs.payeer.accountNumber || '',
          merchantId: gatewaysData.data.configs.payeer.merchantId || '',
          secretKey: '', // لا نعرض secretKey الحالي لأسباب أمنية
          minAmount: gatewaysData.data.configs.payeer.minAmount ?? 0.1,
        });
        break;
      case 'crypto':
        setCryptoForm({
          walletAddress: gatewaysData.data.configs.crypto.walletAddress || '',
          platformName: gatewaysData.data.configs.crypto.platformName || '',
          merchantId: gatewaysData.data.configs.crypto.merchantId || '',
          minAmount: gatewaysData.data.configs.crypto.minAmount ?? 0.1,
        });
        break;
      case 'syriatel':
        setSyriatelForm({
          receptionCodes:
            gatewaysData.data.configs.syriatelCash.receptionCodes.length > 0
              ? [...gatewaysData.data.configs.syriatelCash.receptionCodes]
              : [''],
          minAmount: gatewaysData.data.configs.syriatelCash.minAmount ?? 0.1,
          apiKey: '',
        });
        break;
      case 'shamCash':
        setShamCashForm({
          receptionNumber: gatewaysData.data.configs.shamCash.receptionNumber || '',
          minAmount: gatewaysData.data.configs.shamCash.minAmount ?? 0.1,
          accountAddress: gatewaysData.data.configs.shamCash.accountAddress || '',
          shamGateApiKey: '',
          webhookSecret: '',
        });
        break;
      case 'binance':
        setBinanceForm({
          enabled: gatewaysData.data.configs.binance.enabled || false,
          apiKey: '',
          secretKey: '',
          walletAddress: gatewaysData.data.configs.binance.walletAddress || '',
          minAmount: gatewaysData.data.configs.binance.minAmount ?? 1,
        });
        break;
      case 'oxapay':
        setOxapayForm({
          enabled: gatewaysData.data.configs.oxapay?.enabled || false,
          apiKey: '',
          sandbox: gatewaysData.data.configs.oxapay?.sandbox || false,
          lifetimeMinutes: gatewaysData.data.configs.oxapay?.lifetimeMinutes ?? 60,
          minAmount: gatewaysData.data.configs.oxapay?.minAmount ?? 1,
        });
        break;
      case 'coinex':
        setCoinExForm({
          enabled: gatewaysData.data.configs.coinex.enabled || false,
          accessId: '',
          secretKey: '',
          adminEmail: gatewaysData.data.configs.coinex.adminEmail || '',
          coin: gatewaysData.data.configs.coinex.coin || 'USDT',
          minAmount: gatewaysData.data.configs.coinex.minAmount ?? 1,
        });
        break;
    }
  };

  const handleUpdatePayeer = async () => {
    try {
      await updatePayeer({
        accountNumber: payeerForm.accountNumber || null,
        merchantId: payeerForm.merchantId || null,
        secretKey: payeerForm.secretKey || null,
        minAmount: payeerForm.minAmount,
      }).unwrap();
      toast.success(t('admin.paymentGateways.payeer.updateSuccess'));
      setEditingGateway(null);
      setPayeerForm({ accountNumber: '', merchantId: '', secretKey: '', minAmount: 0.1 });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.paymentGateways.payeer.updateError')
      );
    }
  };

  const handleUpdateCrypto = async () => {
    try {
      await updateCrypto({
        walletAddress: cryptoForm.walletAddress || null,
        platformName: cryptoForm.platformName || null,
        merchantId: cryptoForm.merchantId || null,
        minAmount: cryptoForm.minAmount,
      }).unwrap();
      toast.success(t('admin.paymentGateways.crypto.updateSuccess'));
      setEditingGateway(null);
      setCryptoForm({ walletAddress: '', platformName: '', merchantId: '', minAmount: 0.1 });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.paymentGateways.crypto.updateError')
      );
    }
  };

  const handleUpdateSyriaTel = async () => {
    const codes = syriatelForm.receptionCodes.filter((code) => code.trim() !== '');
    try {
      await updateSyriaTel({
        receptionCodes: codes,
        minAmount: syriatelForm.minAmount,
        apiKey: syriatelForm.apiKey || null,
      }).unwrap();
      toast.success(t('admin.paymentGateways.syriatel.updateSuccess'));
      setEditingGateway(null);
      const savedNewKey = Boolean(syriatelForm.apiKey?.trim());
      setSyriatelForm({ receptionCodes: [''], minAmount: 0.1, apiKey: '' });
      // New API key saved → mint SaasRocket.flo with that key + tenant domain
      if (savedNewKey) {
        try { await handleDownloadAutomateFlo(); } catch (_) { /* toasted in handler */ }
      }
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.paymentGateways.syriatel.updateError')
      );
    }
  };

  const handleUpdateShamCash = async () => {
    try {
      const payload: {
        receptionNumber: string | null;
        minAmount: number;
        accountAddress: string | null;
        shamGateApiKey?: string;
        webhookSecret?: string;
      } = {
        receptionNumber: shamCashForm.receptionNumber || null,
        minAmount: shamCashForm.minAmount,
        accountAddress: shamCashForm.accountAddress || null,
      };
      if (shamCashForm.shamGateApiKey.trim()) {
        payload.shamGateApiKey = shamCashForm.shamGateApiKey.trim();
      }
      if (shamCashForm.webhookSecret.trim()) {
        payload.webhookSecret = shamCashForm.webhookSecret.trim();
      }
      await updateShamCash(payload).unwrap();
      toast.success(t('admin.paymentGateways.shamCash.updateSuccess'));
      setEditingGateway(null);
      setShamCashForm({
        receptionNumber: '',
        minAmount: 0.1,
        accountAddress: '',
        shamGateApiKey: '',
        webhookSecret: '',
      });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.paymentGateways.shamCash.updateError')
      );
    }
  };

  const handleUpdateBinance = async () => {
    try {
      await updateBinance({
        enabled: binanceForm.enabled,
        apiKey: binanceForm.apiKey || null,
        secretKey: binanceForm.secretKey || null,
        walletAddress: binanceForm.walletAddress || null,
        minAmount: binanceForm.minAmount,
      }).unwrap();
      toast.success(t('admin.paymentGateways.binance.updateSuccess'));
      setEditingGateway(null);
      setBinanceForm({ enabled: false, apiKey: '', secretKey: '', walletAddress: '', minAmount: 1 });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.paymentGateways.binance.updateError')
      );
    }
  };

  const handleUpdateOxaPay = async () => {
    try {
      await updateOxaPay({
        enabled: oxapayForm.enabled,
        apiKey: oxapayForm.apiKey || null,
        sandbox: oxapayForm.sandbox,
        minAmount: oxapayForm.minAmount,
      }).unwrap();
      toast.success(t('admin.paymentGateways.oxapay.updateSuccess'));
      setEditingGateway(null);
      setOxapayForm({ enabled: false, apiKey: '', sandbox: false, lifetimeMinutes: 60, minAmount: 1 });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.paymentGateways.oxapay.updateError')
      );
    }
  };

  const handleUpdateCoinEx = async () => {
    try {
      await updateCoinEx({
        enabled: coinexForm.enabled,
        accessId: coinexForm.accessId || null,
        secretKey: coinexForm.secretKey || null,
        adminEmail: coinexForm.adminEmail || null,
        coin: coinexForm.coin,
        minAmount: coinexForm.minAmount,
      }).unwrap();
      toast.success(t('admin.paymentGateways.coinex.updateSuccess'));
      setEditingGateway(null);
      setCoinExForm({ enabled: false, accessId: '', secretKey: '', adminEmail: '', coin: 'USDT', minAmount: 1 });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.paymentGateways.coinex.updateError')
      );
    }
  };

  const addSyriaTelCode = () => {
    setSyriatelForm({
      receptionCodes: [...syriatelForm.receptionCodes, ''],
    });
  };

  const removeSyriaTelCode = (index: number) => {
    const newCodes = syriatelForm.receptionCodes.filter((_, i) => i !== index);
    setSyriatelForm({
      receptionCodes: newCodes.length > 0 ? newCodes : [''],
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="text-center py-16">
          <p className="text-neutral-medium text-xl font-body animate-pulse">
            {t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  const configs = gatewaysData?.data.configs;

  return (
    <div className="min-h-screen animate-slide-up">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-2">
          {t('admin.paymentGateways.title')}
        </h1>
        <p className="text-neutral-medium text-lg font-body">
          {t('admin.paymentGateways.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Payeer — hidden for SaaS tenants */}
        {!IS_TENANT_MODE && (
        <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-heading font-bold text-brand-accent">Payeer</h2>
            {editingGateway !== 'payeer' && (
              <button
                onClick={() => handleStartEdit('payeer')}
                className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
              >
                {t('admin.paymentGateways.edit')}
              </button>
            )}
          </div>

          {editingGateway !== 'payeer' ? (
            <div className="space-y-3">
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.payeer.accountNumber')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.payeer.accountNumber || '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.payeer.merchantId')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.payeer.merchantId || '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.payeer.secretKey')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.payeer.secretKey ? '••••••••' : '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.payeer.minAmount')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.payeer.minAmount ?? 0.1} USD
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.payeer.accountNumber')}
                </label>
                <input
                  type="text"
                  value={payeerForm.accountNumber}
                  onChange={(e) => setPayeerForm({ ...payeerForm, accountNumber: e.target.value })}
                  placeholder={t('admin.paymentGateways.payeer.accountNumberPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.payeer.merchantId')}
                </label>
                <input
                  type="text"
                  value={payeerForm.merchantId}
                  onChange={(e) => setPayeerForm({ ...payeerForm, merchantId: e.target.value })}
                  placeholder={t('admin.paymentGateways.payeer.merchantIdPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.payeer.secretKey')}
                </label>
                <input
                  type="password"
                  value={payeerForm.secretKey}
                  onChange={(e) => setPayeerForm({ ...payeerForm, secretKey: e.target.value })}
                  placeholder={t('admin.paymentGateways.payeer.secretKeyPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono"
                />
                <p className="text-xs text-neutral-medium font-body mt-1">
                  {t('admin.paymentGateways.payeer.secretKeyNote')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.payeer.minAmount')}
                </label>
                <input
                  type="number"
                  value={payeerForm.minAmount}
                  onChange={(e) => setPayeerForm({ ...payeerForm, minAmount: parseFloat(e.target.value) || 0 })}
                  placeholder="0.1"
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleUpdatePayeer}
                  disabled={isUpdatingPayeer}
                  className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUpdatingPayeer ? (
                    <>
                      <ButtonSpinner size="xs" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
                <button
                  onClick={() => {
                    setEditingGateway(null);
                    setPayeerForm({ accountNumber: '', merchantId: '', secretKey: '', minAmount: 0.1 });
                  }}
                  className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Crypto (Cryptomus) — enabled for SaaS tenants (same as Rocket V2) */}
        <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-heading font-bold text-brand-accent">
              {t('admin.paymentGateways.crypto.title')}
            </h2>
            {editingGateway !== 'crypto' && (
              <button
                onClick={() => handleStartEdit('crypto')}
                className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
              >
                {t('admin.paymentGateways.edit')}
              </button>
            )}
          </div>

          {editingGateway !== 'crypto' ? (
            <div className="space-y-3">
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.crypto.walletAddress')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold break-all">
                  {configs?.crypto.walletAddress || '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.crypto.platformName')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.crypto.platformName || '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.crypto.merchantId')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.crypto.merchantId || '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.crypto.minAmount')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.crypto.minAmount ?? 0.1} USD
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.crypto.walletAddress')}
                </label>
                <input
                  type="text"
                  value={cryptoForm.walletAddress}
                  onChange={(e) => setCryptoForm({ ...cryptoForm, walletAddress: e.target.value })}
                  placeholder={t('admin.paymentGateways.crypto.walletAddressPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.crypto.platformName')}
                </label>
                <input
                  type="text"
                  value={cryptoForm.platformName}
                  onChange={(e) => setCryptoForm({ ...cryptoForm, platformName: e.target.value })}
                  placeholder={t('admin.paymentGateways.crypto.platformNamePlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.crypto.merchantId')}
                </label>
                <input
                  type="text"
                  value={cryptoForm.merchantId}
                  onChange={(e) => setCryptoForm({ ...cryptoForm, merchantId: e.target.value })}
                  placeholder={t('admin.paymentGateways.crypto.merchantIdPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.crypto.minAmount')}
                </label>
                <input
                  type="number"
                  value={cryptoForm.minAmount}
                  onChange={(e) => setCryptoForm({ ...cryptoForm, minAmount: parseFloat(e.target.value) || 0 })}
                  placeholder="0.1"
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleUpdateCrypto}
                  disabled={isUpdatingCrypto}
                  className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUpdatingCrypto ? (
                    <>
                      <ButtonSpinner size="xs" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
                <button
                  onClick={() => {
                    setEditingGateway(null);
                    setCryptoForm({ walletAddress: '', platformName: '', merchantId: '', minAmount: 0.1 });
                  }}
                  className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* OxaPay — hidden; SaaS uses Cryptomus (same as Rocket V2) */}
        {false && IS_TENANT_MODE && (
        <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-heading font-bold text-brand-accent">
              {t('admin.paymentGateways.oxapay.title')}
            </h2>
            {editingGateway !== 'oxapay' && (
              <button
                onClick={() => handleStartEdit('oxapay')}
                className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
              >
                {t('admin.paymentGateways.edit')}
              </button>
            )}
          </div>
          {editingGateway !== 'oxapay' ? (
            <div className="space-y-3">
              <div>
                <span className="text-sm text-neutral-medium font-body">{t('admin.paymentGateways.oxapay.enabled')}:</span>
                <span className={`ml-2 text-sm font-body ${configs?.oxapay?.enabled ? 'text-green-400' : 'text-danger'}`}>
                  {configs?.oxapay?.enabled ? t('common.enabled') : t('common.disabled')}
                </span>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">{t('admin.paymentGateways.oxapay.apiKey')}:</span>
                <span className="ml-2 text-sm text-neutral-light font-body">{configs?.oxapay?.apiKey || '-'}</span>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">{t('admin.paymentGateways.oxapay.minAmount')}:</span>
                <span className="ml-2 text-sm text-neutral-light font-body">{configs?.oxapay?.minAmount ?? 1} USD</span>
              </div>
              <p className="text-xs text-neutral-medium font-body">{t('admin.paymentGateways.oxapay.webhookHint')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <label className="flex items-center gap-2 text-sm font-body text-neutral-light">
                <input
                  type="checkbox"
                  checked={oxapayForm.enabled}
                  onChange={(e) => setOxapayForm({ ...oxapayForm, enabled: e.target.checked })}
                />
                {t('admin.paymentGateways.oxapay.enabled')}
              </label>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.oxapay.apiKey')}
                </label>
                <input
                  type="password"
                  value={oxapayForm.apiKey}
                  onChange={(e) => setOxapayForm({ ...oxapayForm, apiKey: e.target.value })}
                  placeholder={t('admin.paymentGateways.oxapay.apiKeyPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
                <p className="text-xs text-neutral-medium mt-1 font-body">{t('admin.paymentGateways.oxapay.apiKeyNote')}</p>
              </div>
              <label className="flex items-center gap-2 text-sm font-body text-neutral-light">
                <input
                  type="checkbox"
                  checked={oxapayForm.sandbox}
                  onChange={(e) => setOxapayForm({ ...oxapayForm, sandbox: e.target.checked })}
                />
                {t('admin.paymentGateways.oxapay.sandbox')}
              </label>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.oxapay.minAmount')}
                </label>
                <input
                  type="number"
                  value={oxapayForm.minAmount}
                  onChange={(e) => setOxapayForm({ ...oxapayForm, minAmount: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleUpdateOxaPay}
                  disabled={isUpdatingOxaPay}
                  className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUpdatingOxaPay ? (
                    <>
                      <ButtonSpinner size="xs" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
                <button
                  onClick={() => {
                    setEditingGateway(null);
                    setOxapayForm({ enabled: false, apiKey: '', sandbox: false, lifetimeMinutes: 60, minAmount: 1 });
                  }}
                  className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
        )}

        {/* SyriaTel */}
        <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-heading font-bold text-brand-accent">
              {t('admin.paymentGateways.syriatel.title')}
            </h2>
            {editingGateway !== 'syriatel' && (
              <button
                onClick={() => handleStartEdit('syriatel')}
                className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
              >
                {t('admin.paymentGateways.edit')}
              </button>
            )}
          </div>

          {editingGateway !== 'syriatel' ? (
            <div className="space-y-2">
              {configs?.syriatelCash.receptionCodes && configs.syriatelCash.receptionCodes.length > 0 ? (
                configs.syriatelCash.receptionCodes.map((code, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-sm text-neutral-medium font-body">
                      {t('admin.paymentGateways.syriatel.receptionCode')} {index + 1}:
                    </span>
                    <span className="text-neutral-light font-heading font-semibold">{code}</span>
                  </div>
                ))
              ) : (
                <p className="text-neutral-medium font-body">-</p>
              )}
              <div className="mt-2">
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.syriatel.minAmount')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.syriatelCash.minAmount ?? 0.1} USD
                </p>
              </div>
              <div className="mt-2">
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.syriatel.apiKey')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.syriatelCash?.apiKey || '-'}
                </p>
              </div>
<div id="syriatel-automate-setup" className="mt-5 p-4 rounded-xl border-2 border-brand-accent/60 bg-brand-accent/10 space-y-3">
                <div className="flex items-center gap-3">
                  <img
                    src="/automate-logo.jpg"
                    alt="Automate"
                    className="w-14 h-14 rounded-xl object-cover flex-shrink-0 border border-neutral-dark"
                  />
                  <div>
                    <h3 className="text-base font-heading font-semibold text-brand-accent">
                      {t('admin.paymentGateways.syriatel.automateTitle')}
                    </h3>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.llamalab.automate"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-brand-accent/90 underline font-body"
                    >
                      {t('admin.paymentGateways.syriatel.automatePlayLink')}
                    </a>
                  </div>
                </div>
                <p className="text-sm text-neutral-light font-body leading-relaxed">
                  {t('admin.paymentGateways.syriatel.automateIntro')}
                </p>
                <ol className="list-decimal list-inside space-y-1.5 text-sm text-neutral-medium font-body">
                  <li>{t('admin.paymentGateways.syriatel.automateStep1')}</li>
                  <li>{t('admin.paymentGateways.syriatel.automateStep2')}</li>
                  <li>{t('admin.paymentGateways.syriatel.automateStep3')}</li>
                  <li>{t('admin.paymentGateways.syriatel.automateStep4')}</li>
                </ol>
                <p className="text-xs text-neutral-medium font-body">
                  {configs?.syriatelCash?.apiKey
                    ? t('admin.paymentGateways.syriatel.automateReadyNote')
                    : t('admin.paymentGateways.syriatel.automateNeedKey')}
                </p>
                <button
                  type="button"
                  disabled={!configs?.syriatelCash?.apiKey || isDownloadingFlo}
                  onClick={() => void handleDownloadAutomateFlo()}
                  className="w-full px-4 py-2.5 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/40 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                >
                  {isDownloadingFlo ? (
                    <>
                      <ButtonSpinner size="xs" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('admin.paymentGateways.syriatel.automateDownload')
                  )}
                </button>
              </div>

              <div className="mt-4 p-3 rounded-xl border border-brand-accent/30 bg-brand-accent/10">
                <p className="text-sm font-medium text-brand-accent mb-1 font-body">
                  {t('admin.paymentGateways.syriatel.webhookUrlLabel')}
                </p>
                <p className="text-sm text-neutral-light font-mono break-all select-all">
                  {configs?.syriatelCash?.webhookUrl || t('admin.paymentGateways.syriatel.webhookUrlMissing')}
                </p>
                <p className="text-xs text-neutral-medium mt-2 font-body">
                  {t('admin.paymentGateways.syriatel.webhookUrlHint')}
                </p>
              </div>

              

            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.syriatel.receptionCodes')}
                </label>
                <div className="space-y-2">
                  {syriatelForm.receptionCodes.map((code, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={code}
                        onChange={(e) => {
                          const newCodes = [...syriatelForm.receptionCodes];
                          newCodes[index] = e.target.value;
                          setSyriatelForm({ receptionCodes: newCodes });
                        }}
                        placeholder={t('admin.paymentGateways.syriatel.receptionCodePlaceholder')}
                        className="flex-1 px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                      />
                      {syriatelForm.receptionCodes.length > 1 && (
                        <button
                          onClick={() => removeSyriaTelCode(index)}
                          className="px-3 py-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30 transition-colors text-sm font-body"
                        >
                          {t('common.remove')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  onClick={addSyriaTelCode}
                  className="mt-2 px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
                >
                  {t('admin.paymentGateways.syriatel.addCode')}
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.syriatel.apiKey')}
                </label>
                <input
                  type="password"
                  value={syriatelForm.apiKey}
                  onChange={(e) => setSyriatelForm({ ...syriatelForm, apiKey: e.target.value })}
                  placeholder={t('admin.paymentGateways.syriatel.apiKeyPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                  autoComplete="new-password"
                />
                <p className="text-xs text-neutral-medium mt-1 font-body">
                  {t('admin.paymentGateways.syriatel.apiKeyNote')}
                </p>
              </div>
              <div className="p-3 rounded-xl border border-brand-accent/30 bg-brand-accent/10">
                <p className="text-sm font-medium text-brand-accent mb-1 font-body">
                  {t('admin.paymentGateways.syriatel.webhookUrlLabel')}
                </p>
                <p className="text-sm text-neutral-light font-mono break-all select-all">
                  {configs?.syriatelCash?.webhookUrl || t('admin.paymentGateways.syriatel.webhookUrlMissing')}
                </p>
                <p className="text-xs text-neutral-medium mt-2 font-body">
                  {t('admin.paymentGateways.syriatel.webhookUrlHint')}
                </p>
              </div>

              <div id="syriatel-automate-setup" className="mt-5 p-4 rounded-xl border-2 border-brand-accent/60 bg-brand-accent/10 space-y-3">
                <div className="flex items-center gap-3">
                  <img
                    src="/automate-logo.jpg"
                    alt="Automate"
                    className="w-14 h-14 rounded-xl object-cover flex-shrink-0 border border-neutral-dark"
                  />
                  <div>
                    <h3 className="text-base font-heading font-semibold text-brand-accent">
                      {t('admin.paymentGateways.syriatel.automateTitle')}
                    </h3>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.llamalab.automate"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-brand-accent/90 underline font-body"
                    >
                      {t('admin.paymentGateways.syriatel.automatePlayLink')}
                    </a>
                  </div>
                </div>
                <p className="text-sm text-neutral-light font-body leading-relaxed">
                  {t('admin.paymentGateways.syriatel.automateIntro')}
                </p>
                <ol className="list-decimal list-inside space-y-1.5 text-sm text-neutral-medium font-body">
                  <li>{t('admin.paymentGateways.syriatel.automateStep1')}</li>
                  <li>{t('admin.paymentGateways.syriatel.automateStep2')}</li>
                  <li>{t('admin.paymentGateways.syriatel.automateStep3')}</li>
                  <li>{t('admin.paymentGateways.syriatel.automateStep4')}</li>
                </ol>
                <p className="text-xs text-neutral-medium font-body">
                  {configs?.syriatelCash?.apiKey
                    ? t('admin.paymentGateways.syriatel.automateReadyNote')
                    : t('admin.paymentGateways.syriatel.automateNeedKey')}
                </p>
                <button
                  type="button"
                  disabled={!configs?.syriatelCash?.apiKey || isDownloadingFlo}
                  onClick={() => void handleDownloadAutomateFlo()}
                  className="w-full px-4 py-2.5 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/40 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                >
                  {isDownloadingFlo ? (
                    <>
                      <ButtonSpinner size="xs" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('admin.paymentGateways.syriatel.automateDownload')
                  )}
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.syriatel.minAmount')}
                </label>
                <input
                  type="number"
                  value={syriatelForm.minAmount}
                  onChange={(e) => setSyriatelForm({ ...syriatelForm, minAmount: parseFloat(e.target.value) || 0 })}
                  placeholder="0.1"
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleUpdateSyriaTel}
                  disabled={isUpdatingSyriaTel}
                  className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUpdatingSyriaTel ? (
                    <>
                      <ButtonSpinner size="xs" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
                <button
                  onClick={() => {
                    setEditingGateway(null);
                    setSyriatelForm({ receptionCodes: [''], minAmount: 0.1, apiKey: '' });
                  }}
                  className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sham Cash */}
        <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-heading font-bold text-brand-accent">
              {t('admin.paymentGateways.shamCash.title')}
            </h2>
            {editingGateway !== 'shamCash' && (
              <button
                onClick={() => handleStartEdit('shamCash')}
                className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
              >
                {t('admin.paymentGateways.edit')}
              </button>
            )}
          </div>

          {editingGateway !== 'shamCash' ? (
            <div className="space-y-3">
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.shamCash.receptionNumber')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold break-all">
                  {configs?.shamCash.receptionNumber || '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.shamCash.accountAddress')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold break-all">
                  {configs?.shamCash.accountAddress || '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.shamCash.shamGateApiKey')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.shamCash.shamGateApiKey || '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.shamCash.shamGateStatus')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.shamCash.shamGateConfigured
                    ? t('admin.paymentGateways.shamCash.shamGateReady')
                    : t('admin.paymentGateways.shamCash.shamGateMissing')}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.shamCash.minAmount')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.shamCash.minAmount ?? 0.1} USD
                </p>
              </div>

              {/* Sham Cash how-to-connect */}
              <div
                id="shamcash-connect-guide"
                className="mt-5 p-4 rounded-xl border-2 border-brand-accent/70 bg-gradient-to-br from-brand-accent/15 to-brand-accent/5 space-y-3 shadow-lg shadow-brand-accent/10"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-brand-accent text-brand-primary text-xl font-heading font-bold">
                    $
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-heading font-semibold text-brand-accent">
                      {t('admin.paymentGateways.shamCash.howToConnectTitle')}
                    </h3>
                    <p className="mt-1 text-sm text-neutral-light font-body leading-relaxed">
                      {t('admin.paymentGateways.shamCash.howToConnectIntro')}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowShamCashGuide(true)}
                  className="w-full px-4 py-3 bg-brand-accent text-brand-primary font-heading font-bold rounded-xl hover:shadow-lg hover:shadow-brand-accent/40 transition-all duration-300 text-sm tracking-wide"
                >
                  {t('admin.paymentGateways.shamCash.howToConnectBtn')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-neutral-medium font-body">
                {t('admin.paymentGateways.shamCash.shamGateHint')}
              </p>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.shamCash.receptionNumber')}
                </label>
                <input
                  type="text"
                  value={shamCashForm.receptionNumber}
                  onChange={(e) => setShamCashForm({ ...shamCashForm, receptionNumber: e.target.value })}
                  placeholder={t('admin.paymentGateways.shamCash.receptionNumberPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.shamCash.accountAddress')}
                </label>
                <input
                  type="text"
                  value={shamCashForm.accountAddress}
                  onChange={(e) => setShamCashForm({ ...shamCashForm, accountAddress: e.target.value })}
                  placeholder={t('admin.paymentGateways.shamCash.accountAddressPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.shamCash.shamGateApiKey')}
                </label>
                <input
                  type="password"
                  value={shamCashForm.shamGateApiKey}
                  onChange={(e) => setShamCashForm({ ...shamCashForm, shamGateApiKey: e.target.value })}
                  placeholder={
                    configs?.shamCash.shamGateApiKey
                      ? t('admin.paymentGateways.shamCash.apiKeyNote')
                      : t('admin.paymentGateways.shamCash.shamGateApiKeyPlaceholder')
                  }
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                  autoComplete="new-password"
                />
                <p className="mt-1 text-xs text-neutral-medium font-body">
                  {t('admin.paymentGateways.shamCash.apiKeyNote')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.shamCash.webhookSecret')}
                </label>
                <input
                  type="password"
                  value={shamCashForm.webhookSecret}
                  onChange={(e) => setShamCashForm({ ...shamCashForm, webhookSecret: e.target.value })}
                  placeholder={t('admin.paymentGateways.shamCash.webhookSecretPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                  autoComplete="new-password"
                />
                <p className="mt-1 text-xs text-neutral-medium font-body">
                  {t('admin.paymentGateways.shamCash.webhookSecretNote')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.shamCash.minAmount')}
                </label>
                <input
                  type="number"
                  value={shamCashForm.minAmount}
                  onChange={(e) => setShamCashForm({ ...shamCashForm, minAmount: parseFloat(e.target.value) || 0 })}
                  placeholder="0.1"
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleUpdateShamCash}
                  disabled={isUpdatingShamCash}
                  className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUpdatingShamCash ? (
                    <>
                      <ButtonSpinner size="xs" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
                <button
                  onClick={() => {
                    setEditingGateway(null);
                    setShamCashForm({
                      receptionNumber: '',
                      minAmount: 0.1,
                      accountAddress: '',
                      shamGateApiKey: '',
                      webhookSecret: '',
                    });
                  }}
                  className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Binance */}
        <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-heading font-bold text-brand-accent">
              {t('admin.paymentGateways.binance.title')}
            </h2>
            {editingGateway !== 'binance' && (
              <button
                onClick={() => handleStartEdit('binance')}
                className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
              >
                {t('admin.paymentGateways.edit')}
              </button>
            )}
          </div>

          {editingGateway !== 'binance' ? (
            <div className="space-y-3">
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.binance.enabled')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.binance.enabled ? t('common.enabled') : t('common.disabled')}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.binance.walletAddress')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold break-all">
                  {configs?.binance.walletAddress || '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.binance.minAmount')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.binance.minAmount ?? 1} USD
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.binance.apiKey')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.binance.apiKey ? '••••••••' : '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.binance.secretKey')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.binance.secretKey ? '••••••••' : '-'}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-neutral-medium font-body">
                  {t('admin.paymentGateways.binance.enabled')}
                </label>
                <input
                  type="checkbox"
                  checked={binanceForm.enabled}
                  onChange={(e) => setBinanceForm({ ...binanceForm, enabled: e.target.checked })}
                  className="w-5 h-5 rounded bg-neutral-dark border border-neutral-dark focus:ring-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.binance.walletAddress')}
                </label>
                <input
                  type="text"
                  value={binanceForm.walletAddress}
                  onChange={(e) => setBinanceForm({ ...binanceForm, walletAddress: e.target.value })}
                  placeholder={t('admin.paymentGateways.binance.walletAddressPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.binance.minAmount')}
                </label>
                <input
                  type="number"
                  value={binanceForm.minAmount}
                  onChange={(e) => setBinanceForm({ ...binanceForm, minAmount: e.target.value === '' ? 1 : parseFloat(e.target.value) || 0 })}
                  placeholder="1"
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.binance.apiKey')}
                </label>
                <input
                  type="password"
                  value={binanceForm.apiKey}
                  onChange={(e) => setBinanceForm({ ...binanceForm, apiKey: e.target.value })}
                  placeholder={t('admin.paymentGateways.binance.apiKeyPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono text-sm"
                />
                <p className="text-xs text-neutral-medium font-body mt-1">
                  {t('admin.paymentGateways.binance.apiKeyNote')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.binance.secretKey')}
                </label>
                <input
                  type="password"
                  value={binanceForm.secretKey}
                  onChange={(e) => setBinanceForm({ ...binanceForm, secretKey: e.target.value })}
                  placeholder={t('admin.paymentGateways.binance.secretKeyPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono text-sm"
                />
                <p className="text-xs text-neutral-medium font-body mt-1">
                  {t('admin.paymentGateways.binance.secretKeyNote')}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleUpdateBinance}
                  disabled={isUpdatingBinance}
                  className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUpdatingBinance ? (
                    <>
                      <ButtonSpinner size="xs" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
                <button
                  onClick={() => {
                    setEditingGateway(null);
                    setBinanceForm({ enabled: false, apiKey: '', secretKey: '', walletAddress: '', minAmount: 1 });
                  }}
                  className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* CoinEx Card — hidden (commented out for easy restore) */}
        {false && (
        <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-heading font-bold text-brand-accent">
              {t('admin.paymentGateways.coinex.title')}
            </h3>
            {editingGateway !== 'coinex' && (
              <button
                onClick={() => handleStartEdit('coinex')}
                className="px-4 py-2 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-all duration-300 font-heading font-semibold text-sm"
              >
                {t('admin.paymentGateways.edit')}
              </button>
            )}
          </div>

          {editingGateway !== 'coinex' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-neutral-medium font-body">{t('admin.paymentGateways.coinex.enabled')}:</span>
                <span className={`ml-2 text-sm font-body ${gatewaysData?.data.configs.coinex.enabled ? 'text-green-400' : 'text-danger'}`}>
                  {gatewaysData?.data.configs.coinex.enabled ? t('common.yes') : t('common.no')}
                </span>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">{t('admin.paymentGateways.coinex.adminEmail')}:</span>
                <span className="ml-2 text-sm text-neutral-light font-body font-mono">
                  {gatewaysData?.data.configs.coinex.adminEmail || '-'}
                </span>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">{t('admin.paymentGateways.coinex.coin')}:</span>
                <span className="ml-2 text-sm text-neutral-light font-body">
                  {gatewaysData?.data.configs.coinex.coin || 'USDT'}
                </span>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">{t('admin.paymentGateways.coinex.minAmount')}:</span>
                <span className="ml-2 text-sm text-neutral-light font-body">
                  {gatewaysData?.data.configs.coinex.minAmount ?? 1}
                </span>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">{t('admin.paymentGateways.coinex.accessId')}:</span>
                <span className="ml-2 text-sm text-neutral-light font-body">
                  {gatewaysData?.data.configs.coinex.accessId ? '••••••••' : '-'}
                </span>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">{t('admin.paymentGateways.coinex.secretKey')}:</span>
                <span className="ml-2 text-sm text-neutral-light font-body">
                  {gatewaysData?.data.configs.coinex.secretKey ? '••••••••' : '-'}
                </span>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={coinexForm.enabled}
                    onChange={(e) => setCoinExForm({ ...coinexForm, enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-neutral-dark rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-brand-accent"></div>
                </label>
                <span className="text-sm text-neutral-light font-body">
                  {t('admin.paymentGateways.coinex.enabled')}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.coinex.adminEmail')}
                </label>
                <input
                  type="email"
                  value={coinexForm.adminEmail}
                  onChange={(e) => setCoinExForm({ ...coinexForm, adminEmail: e.target.value })}
                  placeholder={t('admin.paymentGateways.coinex.adminEmailPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.coinex.coin')}
                </label>
                <input
                  type="text"
                  value={coinexForm.coin}
                  onChange={(e) => setCoinExForm({ ...coinexForm, coin: e.target.value })}
                  placeholder="USDT"
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.coinex.minAmount')}
                </label>
                <input
                  type="number"
                  value={coinexForm.minAmount}
                  onChange={(e) => setCoinExForm({ ...coinexForm, minAmount: e.target.value === '' ? 1 : parseFloat(e.target.value) || 0 })}
                  placeholder="1"
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.coinex.accessId')}
                </label>
                <input
                  type="password"
                  value={coinexForm.accessId}
                  onChange={(e) => setCoinExForm({ ...coinexForm, accessId: e.target.value })}
                  placeholder={t('admin.paymentGateways.coinex.accessIdPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono text-sm"
                />
                <p className="text-xs text-neutral-medium font-body mt-1">
                  {t('admin.paymentGateways.coinex.accessIdNote')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.coinex.secretKey')}
                </label>
                <input
                  type="password"
                  value={coinexForm.secretKey}
                  onChange={(e) => setCoinExForm({ ...coinexForm, secretKey: e.target.value })}
                  placeholder={t('admin.paymentGateways.coinex.secretKeyPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono text-sm"
                />
                <p className="text-xs text-neutral-medium font-body mt-1">
                  {t('admin.paymentGateways.coinex.secretKeyNote')}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleUpdateCoinEx}
                  disabled={isUpdatingCoinEx}
                  className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUpdatingCoinEx ? (
                    <>
                      <ButtonSpinner size="xs" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
                <button
                  onClick={() => {
                    setEditingGateway(null);
                    setCoinExForm({ enabled: false, accessId: '', secretKey: '', adminEmail: '', coin: 'USDT', minAmount: 1 });
                  }}
                  className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>)}
      </div>

      {/* Sham Cash connect modal */}
      {showShamCashGuide ? (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowShamCashGuide(false)}
        >
          <div
            className="relative w-full max-w-lg rounded-2xl border border-brand-accent/40 bg-neutral-dark p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-heading font-bold text-brand-accent mb-3">
              {t('admin.paymentGateways.shamCash.howToConnectTitle')}
            </h3>
            <p className="text-sm text-neutral-light font-body leading-relaxed mb-4">
              {t('admin.paymentGateways.shamCash.howToConnectIntro')}
            </p>
            <ol className="list-decimal list-inside space-y-2.5 text-sm text-neutral-medium font-body mb-4">
              <li>{t('admin.paymentGateways.shamCash.howToConnectStep1')}</li>
              <li>{t('admin.paymentGateways.shamCash.howToConnectStep2')}</li>
              <li>{t('admin.paymentGateways.shamCash.howToConnectStep3')}</li>
            </ol>
            <p className="text-xs text-neutral-medium font-body leading-relaxed mb-5 rounded-lg border border-neutral-dark bg-black/20 p-3">
              {t('admin.paymentGateways.shamCash.howToConnectSupport')}
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <a
                href="https://shamapi.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-center px-4 py-2.5 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/40 transition-all text-sm"
              >
                {t('admin.paymentGateways.shamCash.howToConnectOpenSite')}
              </a>
              <button
                type="button"
                onClick={() => setShowShamCashGuide(false)}
                className="flex-1 px-4 py-2.5 border border-neutral-medium/40 text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-colors text-sm"
              >
                {t('admin.paymentGateways.shamCash.howToConnectClose')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

